import { SidControlCodeData } from 'common/types/sid-control-code-data';
import * as Config from 'Config';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ControlCodeBase } from '../';
import { SidSocketInitMessage } from '../../common/types/sid-socket-init-message';
import { SIDSessionResultCode, SidSocketResponse } from '../../common/types/sid-socket-response';
import ClientDataContext from '../../store/client-data-context';
import { QrCode } from '../QrCode/QrCode';
import isMobileDevice from '../../utils/mobile-check.util';

const getSimplifiedErrorResultCode = (resultCode: SIDSessionResultCode) => {
  if (
    [
      SIDSessionResultCode.USER_REFUSED,
      SIDSessionResultCode.USER_REFUSED_CERT_CHOICE,
      SIDSessionResultCode.USER_REFUSED_DISPLAYTEXTANDPIN,
      SIDSessionResultCode.USER_REFUSED_VC_CHOICE,
      SIDSessionResultCode.USER_REFUSED_CONFIRMATIONMESSAGE,
      SIDSessionResultCode.USER_REFUSED_CONFIRMATIONMESSAGE_WITH_VC_CHOICE,
      SIDSessionResultCode.USER_REFUSED_INTERACTION,
    ].includes(resultCode)
  ) {
    return 'USER_ERROR';
  } else if ((resultCode as unknown as string) === 'SERVER_ERROR') {
    return 'INTERNAL_ERROR';
  } else if (SIDSessionResultCode.REQUIRED_INTERACTION_NOT_SUPPORTED_BY_APP == resultCode) {
    return 'REQUIRED_INTERACTION_NOT_SUPPORTED_BY_APP';
  } else if (SIDSessionResultCode.DOCUMENT_UNUSABLE == resultCode) {
    return 'DOCUMENT_UNUSABLE';
  } else if (SIDSessionResultCode.WRONG_VC == resultCode) {
    return 'WRONG_VC';
  } else if (SIDSessionResultCode.TIMEOUT == resultCode) {
    return 'TIMEOUT';
  }
  return 'INTERNAL_ERROR';
};

interface ControlCodeProps {
  controlCodeData: SidControlCodeData;
  displayQrCode: boolean;
  closeControlCode: () => void;
  setSocketErrorCode: (value: string) => void;
  socketErrorCode?: string;
  setSidErrorOrigin: (v: 'qr' | 'personal' | null) => void;
}

export const SidControlCode = ({
  controlCodeData,
  displayQrCode,
  closeControlCode,
  setSocketErrorCode,
  socketErrorCode,
  setSidErrorOrigin,
}: ControlCodeProps) => {
  const [controlCode, setControlCode] = useState('');
  const [qrCode, setQrCode] = useState(null as unknown as string);

  const mountedRef = useRef(true);
  const socketRef = useRef<WebSocket | null>(null);

  const initSentRef = useRef(false);
  const deviceLinkOpenedRef = useRef(false);
  const reconnectedRef = useRef(false);
  const finishedRef = useRef(false);
  const tabHiddenCloseRef = useRef(false);

  const clientCtx = useContext(ClientDataContext);
  const { i18n } = useTranslation();
  const isMobile = isMobileDevice();

  useEffect(() => {
    mountedRef.current = true;
    setSocketErrorCode('');

    createWebSocket();
    window.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      mountedRef.current = false;
      window.removeEventListener('visibilitychange', onVisibilityChange);
      closeWebSocket('component_unmount');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function isWebSocketOpen() {
    return mountedRef.current
      && socketRef.current != null
      && socketRef.current.readyState === WebSocket.OPEN;
  }

  function closeWebSocket(reason?: string, code: number = 1000) {
    try {
      const ws = socketRef.current;
      socketRef.current = null;
      ws?.close(code, reason);
    } catch {}
  }

  function getInitMessage() {
    if (controlCodeData.deviceLink) {
      return JSON.stringify({
        _csrf: clientCtx.client._csrf,
        lang: i18n.language.split('-')[0],
        flow: isMobile ? 'device-link' : 'device-link-qr',
      } as SidSocketInitMessage);
    } else if (controlCodeData.authData) {
      return JSON.stringify({
        _csrf: clientCtx.client._csrf,
        country: controlCodeData.authData.authCountryCode,
        id_number: controlCodeData.authData.personCode,
        lang: i18n.language.split('-')[0],
      } as SidSocketInitMessage);
    }
    return null;
  }

  function sendInitMessage() {
    if (!isWebSocketOpen() || initSentRef.current) return;

    console.log('Sending init message');
    initSentRef.current = true;
    const initMessage = getInitMessage();
    if (initMessage) {
      socketRef.current!.send(initMessage);
    }
  }

  function createWebSocket() {
    closeWebSocket('recreating_socket');

    const ws = new WebSocket(Config.SID_AUTH_SOCKET_URL);
    socketRef.current = ws;
    finishedRef.current = false;
    initSentRef.current = false;
    tabHiddenCloseRef.current = false;

    ws.onopen = () => {
      console.log('WebSocket opened');
      setTimeout(sendInitMessage, 200);
    };

    ws.onmessage = (message) => {
      if (!mountedRef.current) return;

      const response: SidSocketResponse = JSON.parse(message.data);

      if (response.control_code != null) {
        setControlCode(response.control_code);
      } else if (response.device_link != null) {
        if (isMobile) {
          if (!deviceLinkOpenedRef.current) {
            console.log('Redirecting to device link');
            deviceLinkOpenedRef.current = true;
            closeWebSocket('open_device_link');
            window.location.href = response.device_link;
          }
        } else {
          console.log('Setting QR code data');
          setQrCode(response.device_link);
        }
      } else if (response.finished && response.result != undefined) {
        finishedRef.current = true;
        if (response.result === SIDSessionResultCode.OK) {
          closeWebSocket('SID result OK, redirecting');
          window.location.href = Config.AUTHORIZE_URL + '?' + clientCtx.client.authorizeRequestParams;
        } else {
          showError(getSimplifiedErrorResultCode(response.result));
        }
      } else if (response.error) {
        showError(response.error);
      }
    };

    ws.onerror = (e) => {
      console.log(`WebSocket error: ${JSON.stringify(e)}`);
      showError('INTERNAL_ERROR');
    };

    ws.onclose = () => {
      if (!mountedRef.current) return;
      setTimeout(() => {
        if (!mountedRef.current || finishedRef.current || socketRef.current !== null || deviceLinkOpenedRef.current || tabHiddenCloseRef.current) return;
        showError('INTERNAL_ERROR');
      }, 0);
    };
  }

  function onVisibilityChange() {
    if (!mountedRef.current || finishedRef.current || !isMobile) return;

    if (document.hidden) {
      if (isWebSocketOpen()) {
        console.log('Tab hidden, closing WebSocket');
        tabHiddenCloseRef.current = true;
        closeWebSocket('tab_hidden');
      }
      return;
    }

    const canReconnect = !isWebSocketOpen() && isMobile && initSentRef.current
      && (deviceLinkOpenedRef.current || !reconnectedRef.current);

    if (canReconnect) {
      console.log('Tab focused, reconnecting');
      reconnectedRef.current = true;
      setTimeout(createWebSocket, 500);
    }
  }

  const cancel = () => {
    initSentRef.current = false;
    reconnectedRef.current = false;
    showError(getSimplifiedErrorResultCode(SIDSessionResultCode.USER_REFUSED), 'user_cancel');
    setQrCode(null as unknown as string);
  };

  function showError(code: string, reason: string = 'show_error') {
    finishedRef.current = true;
    setSocketErrorCode(code);
    setSidErrorOrigin(controlCodeData.deviceLink ? 'qr' : 'personal');
    closeWebSocket(reason);
    closeControlCode();
  }

  return (
    <>
      {displayQrCode ? (
        <QrCode data={qrCode} onCancel={cancel} socketErrorCode={socketErrorCode} />
      ) : (
        <ControlCodeBase authMethod="sid"
                         isDeviceLink={!!controlCodeData.deviceLink}
                         controlCode={controlCode}
                         onCancel={cancel} />
      )}
    </>
  );
};
